<template>
  <div class="signal-composer">
    <div class="tabs" role="tablist">
      <div v-for="tab in visibleTabs" :key="tab.id" role="tab" tabindex="0" class="tab"
        :class="{ active: activeTab === tab.id }" :aria-selected="activeTab === tab.id" :title="t(tab.label)"
        @click="activeTab = tab.id" @keydown.enter="activeTab = tab.id">
        <font-awesome-icon :icon="['fas', tab.icon]" />
        <span v-if="activeTab === tab.id" class="tab-label">{{ t(tab.label) }}</span>
      </div>
    </div>

    <div class="tab-panel" role="tabpanel">
      <div v-if="activeTab === 'player'" class="signal-buttons">
        <button v-for="player in players" :key="player.id || player.name" type="button" class="signal-button"
          @click="signalsStore.addItem({ kind: 'player', value: player.id || player.name })">
          <font-awesome-icon :icon="['fas', 'user']" />
          {{ player.name }}
          <small v-if="player.role">({{ player.realRole?.name || player.role.name }})</small>
        </button>
      </div>

      <div v-else-if="activeTab === 'role'" class="signal-buttons">
        <button v-for="role in roles" :key="role.id" type="button" class="signal-button"
          @click="signalsStore.addItem({ kind: 'role', value: role.id })">
          <img class="signal-button-icon" :src="getRoleImage(role)" alt="" />
          {{ role.name }}
        </button>
      </div>

      <div v-else-if="activeTab === 'number'" class="signal-buttons">
        <button v-for="n in numbers" :key="n" type="button" class="signal-button number"
          @click="signalsStore.addItem({ kind: 'number', value: n })">
          {{ n }}
        </button>
      </div>

      <div v-else-if="activeTab === 'yesNo'" class="signal-buttons">
        <button type="button" class="signal-button big yes" @click="signalsStore.addItem({ kind: 'yes' })">
          <font-awesome-icon :icon="['fas', 'check']" />
          {{ t("modal.signals.yes") }}
        </button>
        <button type="button" class="signal-button big no" @click="signalsStore.addItem({ kind: 'no' })">
          <font-awesome-icon :icon="['fas', 'times']" />
          {{ t("modal.signals.no") }}
        </button>
      </div>

      <div v-else-if="activeTab === 'alignment'" class="signal-buttons">
        <button v-for="alignment in alignments" :key="alignment" type="button" class="signal-button big"
          @click="signalsStore.addItem({ kind: 'alignment', value: alignment })">
          <img class="signal-button-icon" :src="getRoleImage({ id: alignment } as Role)" alt="" />
          {{ t(`modal.signals.alignment.${alignment}`) }}
        </button>
      </div>

      <div v-else-if="activeTab === 'type'" class="signal-buttons">
        <button v-for="type in characterTypes" :key="type" type="button" class="signal-button big"
          @click="signalsStore.addItem({ kind: 'type', value: type })">
          <img class="signal-button-icon" :src="teamIcon(type)" alt="" />
          {{ t(`modal.signals.type.${type}`) }}
        </button>
      </div>

      <div v-else-if="activeTab === 'message'" class="signal-message-tab">
        <!-- Storyteller only: prewritten messages -->
        <div v-if="!session.isPlayerOrSpectator && cards.length" class="signal-buttons">
          <button v-for="card in cards" :key="card" type="button" class="signal-button card"
            @click="signalsStore.addItem({ kind: 'message', value: card })">
            <font-awesome-icon :icon="['fas', 'sticky-note']" />
            {{ card }}
          </button>
        </div>
        <div class="signal-buttons">
          <input v-model="messageValue" type="text" class="signal-message"
            :placeholder="t('modal.signals.category.message')" @keyup.enter="addMessage" />
          <button type="button" class="signal-button" :disabled="!messageValue.trim()" @click="addMessage">
            <font-awesome-icon :icon="['fas', 'paper-plane']" />
          </button>
        </div>
      </div>

      <!-- Storyteller only: send the draft as a request, answerable with the chosen tabs -->
      <div v-else-if="activeTab === 'request'" class="signal-request-config">
        <p class="signal-hint">{{ t("modal.signals.answerTabs") }}</p>
        <div class="signal-buttons">
          <button v-for="tab in allTabs" :key="tab.id" type="button" class="signal-button"
            :class="{ selected: requestTabs.includes(tab.id) }" :aria-pressed="requestTabs.includes(tab.id)"
            @click="toggleRequestTab(tab.id)">
            <font-awesome-icon class="signal-checkbox"
              :icon="['fas', requestTabs.includes(tab.id) ? 'check-square' : 'square']" />
            <font-awesome-icon :icon="['fas', tab.icon]" />
            {{ t(tab.label) }}
          </button>
        </div>
        <div class="signal-buttons">
          <button type="button" class="signal-button send" :disabled="!requestTabs.length"
            @click="signalsStore.send('request', requestTabs)">
            <font-awesome-icon :icon="['fas', 'paper-plane']" />
            {{ t("modal.signals.sendRequest") }}
          </button>
        </div>
      </div>
    </div>

    <div class="signal-draft">
      <SignalChip v-for="(item, index) in signalsStore.draft" :key="index" :item="item" removable
        @remove="signalsStore.removeItem(index)" />
      <span v-if="!signalsStore.draft.length" class="signal-draft-empty">
        {{ t("modal.signals.draftEmpty") }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { SignalChip } from "@/components";
import { getRoleImage } from "@/services/assets";
import {
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useSessionStore,
  useSignalsStore,
} from "@/stores";
import type { Role, SignalAlignment, SignalCharacterType, SignalTab } from "@/types";

type ComposerTab = SignalTab | "request";

// Subset of answer tabs to show (order stays as in allTabs). Defaults to all of them.
const {
  tabs = ["player", "role", "number", "yesNo", "alignment", "type", "message"],
} = defineProps<{ tabs?: SignalTab[] | undefined }>();

const allTabs: { id: SignalTab; icon: string; label: string }[] = [
  { id: "player", icon: "user", label: "modal.signals.category.player" },
  { id: "role", icon: "theater-masks", label: "modal.signals.category.role" },
  { id: "number", icon: "hashtag", label: "modal.signals.category.number" },
  { id: "yesNo", icon: "thumbs-up", label: "modal.signals.category.yesNo" },
  { id: "alignment", icon: "yin-yang", label: "modal.signals.category.alignment" },
  { id: "type", icon: "users", label: "modal.signals.category.type" },
  { id: "message", icon: "comment", label: "modal.signals.category.message" },
];
const requestTab: { id: ComposerTab; icon: string; label: string } = {
  id: "request",
  icon: "question",
  label: "modal.signals.requestTitle",
};

const characterTypes: SignalCharacterType[] = [
  "townsfolk",
  "outsider",
  "minion",
  "demon",
  "traveler",
];
const alignments: SignalAlignment[] = ["good", "evil"];

const grimoire = useGrimoireStore();
const playersStore = usePlayersStore();
const session = useSessionStore();
const signalsStore = useSignalsStore();
const t = useLocaleStore().t;

const visibleTabs = computed(() => [
  ...allTabs.filter((tab) => tabs.includes(tab.id)),
  ...(session.isPlayerOrSpectator ? [] : [requestTab]),
]);
const activeTab = ref<ComposerTab>(visibleTabs.value[0]?.id ?? "player");
// A new request may allow different tabs while the composer stays mounted.
watch(visibleTabs, (list) => {
  if (!list.some((tab) => tab.id === activeTab.value)) {
    activeTab.value = list[0]?.id ?? "player";
  }
});

const messageValue = ref("");
const requestTabs = ref<SignalTab[]>([]);

// t() hands back the raw array for list entries, or the key itself when missing.
const cards = computed(() => {
  const value: unknown = t("modal.signals.cards");
  return Array.isArray(value)
    ? value.filter((card): card is string => typeof card === "string")
    : [];
});

const players = computed(() => playersStore.players.filter((p) => p.name));

// Script order: grouped by character type, then alphabetically.
const teamRank = (role: Role) => {
  const rank = characterTypes.indexOf(role.team as SignalCharacterType);
  return rank === -1 ? characterTypes.length : rank;
};
const roles = computed(() =>
  Array.from(grimoire.roles.values())
    .filter((role) => role.id && role.name)
    .sort((a, b) => teamRank(a) - teamRank(b) || (a.name || "").localeCompare(b.name || "")),
);

const numbers = computed(() =>
  Array.from({ length: Math.max(10, players.value.length) + 1 }, (_, i) => i),
);

function teamIcon(team: SignalCharacterType) {
  return new URL(`../assets/icons/${team}.webp`, import.meta.url).href;
}

function addMessage() {
  const text = messageValue.value.trim();
  if (!text) return;
  signalsStore.addItem({ kind: "message", value: text });
  messageValue.value = "";
}

function toggleRequestTab(id: SignalTab) {
  const selected = new Set(requestTabs.value);
  if (selected.has(id)) selected.delete(id);
  else selected.add(id);
  // Keep the display order so the player sees tabs in the usual order.
  requestTabs.value = allTabs.map((tab) => tab.id).filter((tabId) => selected.has(tabId));
}
</script>

<style scoped lang="scss">
.signal-composer {
  display: flex;
  flex-direction: column;

  > .signal-draft {
    margin-top: 0.75em;
  }
}

.tabs {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #666;
}

.tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
  flex: 1;
  min-width: 2.4em;
  padding: 0.35em 0.5em;
  border: 1px solid grey;
  border-bottom: none;
  border-radius: 5px 5px 0 0;
  font-size: 1.15em;
  cursor: pointer;
  user-select: none;
  transition: background 250ms;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &.active {
    flex: 2.5;
    background: linear-gradient(rgb(31, 101, 255) 0%, rgba(0, 0, 0, 0.5) 100%);
  }
}

.tab-label {
  font-size: 0.8em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-panel {
  max-height: 45vh;
  overflow-y: auto;
  scrollbar-width: thin;
  background: #6666;
  border: 1px solid #fff3;
  border-top: none;
}

.signal-buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
}

.signal-button {
  display: inline-flex;
  align-items: center;

  &.number {
    min-width: 2.4em;
    justify-content: center;
  }

  &.big {
    font-size: 1.4rem;
  }

  &.card {
    white-space: normal;
    text-align: left;
  }

  &.yes svg {
    color: #4caf50;
  }

  &.no svg {
    color: #f44336;
  }

  &.send svg {
    color: rgb(31, 101, 255);
  }

  &.selected {
    border-color: rgb(31, 101, 255);
    box-shadow:
      inset 0 1px 1px #9c9c9c,
      0 0 10px rgb(31, 101, 255);

    .signal-checkbox {
      color: rgb(31, 101, 255);
    }
  }

  &[disabled] svg {
    color: inherit;
  }
}

.signal-button-icon {
  width: 1.8em;
  height: 1.8em;
  object-fit: contain;
}

.signal-message-tab {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.signal-message {
  flex: 1;
  min-width: 8em;
  background: #fff9;
}

.signal-request-config {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding: 0.5em;
}

.signal-hint {
  margin: 0;
  text-align: center;
  opacity: 0.8;
}

.signal-draft {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 2.6em;
  padding: 0.4em;
  border: 1px dashed var(--border-color);
  border-radius: 0.5em;
}

.signal-draft-empty {
  opacity: 0.6;
  font-style: italic;
  padding: 0 0.4em;
}
</style>
